import { Box, Card, Text, Tag, Icon, Skeleton } from '@nimbus-ds/components';
import {
  CheckIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import dayjs from 'dayjs';
import ModalAside from 'App/components/lab/ModalAside';
import { useSupportChat } from 'App/components/SupportChatContext';
import { HeaderContent, HeaderTop } from 'commons/components';
import { openWindow } from 'commons/utils/window';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { useGetDomainLifecycleDetailsQuery } from '../../../../domainsApi';
import './DomainInfoModal.scss';

interface DomainInfoModalProps {
  domainName: string;
  show: boolean;
  onClose: () => void;
}

interface MenuItem {
  label: string;
  onClick: () => void;
  icon: JSX.Element;
}

export function DomainInfoModal({
  domainName,
  show,
  onClose,
}: Readonly<DomainInfoModalProps>) {
  const t = useTranslationConfigurations('domains.infoModal');

  const { openChatOnly } = useSupportChat();

  const { data: domainDetails, isLoading } = useGetDomainLifecycleDetailsQuery(
    domainName,
    {
      skip: !domainName || !show,
    },
  );

  const handleOpenChat = () => {
    onClose();
    openChatOnly();
  };

  const handleGoToHelp = () => {
    openWindow(
      'https://ayuda.tiendanube.com/es_MX/175782-abc-del-dominio-propio/preguntas-frecuentes-sobre-la-compra-de-dominios-en-tiendanube',
      true,
    );
  };

  const expirationDate = domainDetails?.expiresAt
    ? dayjs(domainDetails.expiresAt).format('DD/MM/YYYY')
    : '';

  const isActive = domainDetails?.lifecycleStatus === 'ACTIVE';
  const statusText = isActive ? t('statusActive') : t('statusConfiguring');
  const statusAppearance = isActive ? 'success' : 'warning';

  const menuItems: MenuItem[] = [
    {
      label: t('menuItems.renew'),
      onClick: handleGoToHelp,
      icon: <Icon color="primary-interactive" source={<ExternalLinkIcon />} />,
    },
    {
      label: t('menuItems.transfer'),
      onClick: handleOpenChat,
      icon: <Icon color="primary-interactive" source={<ChevronRightIcon />} />,
    },
    {
      label: t('menuItems.dns'),
      onClick: handleOpenChat,
      icon: <Icon color="primary-interactive" source={<ChevronRightIcon />} />,
    },
  ];

  return (
    <ModalAside isOpen={show} onDidDismiss={onClose}>
      <HeaderTop navigation={{ onClick: onClose }} />
      <HeaderContent title={t('title')} />

      <Box>
        <Card backgroundColor="neutral-background">
          <Box display="flex" flexDirection="column" gap="2">
            <Text fontSize="base" fontWeight="medium">
              {domainName}
            </Text>
            {isLoading ? (
              <Skeleton width="200px" height="16px" />
            ) : (
              expirationDate && (
                <Text fontSize="caption" color="neutral-textLow">
                  {t('expiresOn', {
                    date: expirationDate,
                    interpolation: { escapeValue: false },
                  })}
                </Text>
              )
            )}
            <Box>
              {isLoading ? (
                <Skeleton width="80px" height="24px" />
              ) : (
                <Tag appearance={statusAppearance}>
                  <Box display="flex" alignItems="center" gap="1">
                    <CheckIcon />
                    {statusText}
                  </Box>
                </Tag>
              )}
            </Box>
          </Box>
        </Card>

        <Box marginTop="4" data-style="stratus--domain-info-modal">
          <Card backgroundColor="neutral-background" padding="none">
            <DataList>
              {menuItems.map((item) => (
                <DataList.Row
                  key={item.label}
                  onClick={item.onClick}
                  cursor="pointer"
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Text fontSize="base">{item.label}</Text>
                    {item.icon}
                  </Box>
                </DataList.Row>
              ))}
            </DataList>
          </Card>
        </Box>
      </Box>
    </ModalAside>
  );
}
